import { Inventory2Outlined, LinkOutlined, VideocamOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { COLORS } from "assets";
import { ActivationModelGrid, BoxSelect, ConnectionTypeSelect, FormModalActions, MyModal, TextInput } from "components";
import { ENDPOINTS, filterModelsForUI, normalizeActiveModelMedia } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ActiveModel, CameraModel, CameraStatuses, ModelTypes, RTSPSources } from "types";
import { cameraModalWidth, fieldSx, iconSx, sectionHintSx, sectionTitleSx } from "./formShared";

const defaultValue = {
  rtsp: "",
  esn: "",
  token: "",
  name: "",
  boxId: "",
  webHooks: ""
};

interface IEdit {
  camera: CameraModel;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EditCamera({ camera, onSuccess, onClose }: IEdit) {
  const [activeModels, setActiveModels] = useState<ActiveModel[]>([]);
  const [enabledModels, setEnabledModels] = useState<ModelTypes[]>(() =>
    camera.box?.site ? filterModelsForUI(camera.box.site.enabledModels) : []
  );
  const [modelsReady, setModelsReady] = useState(Boolean(camera.box?.site));
  const [state, setState] = useState({
    rtsp: camera.rtsp || "",
    esn: (camera as CameraModel & { cameraESN?: string }).cameraESN || "",
    token: camera.acc_token || camera.eagleEyeToken || "",
    name: camera.name || "",
    boxId: camera.boxId || camera.box?._id || "",
    webHooks: camera.webHooks || ""
  });
  const [currentStatus, setCurrentStatus] = useState<CameraStatuses | undefined>(camera.cameraStatus);

  const t = useTranslation();
  const [rtspSource, setRTSPSource] = useState<RTSPSources>(
    camera.rtspSource
      ? camera.rtspSource
      : camera.acc_token
        ? "acc_token"
        : camera.eagleEyeToken
          ? "eagleEyeToken"
          : "manual"
  );
  const [errors, setErrors] = useState(defaultValue);
  const { isLoading, response, error, callFetch: callFetchPut } = useFetch("PUT");
  const {
    isLoading: isStatusLoading,
    response: statusResponse,
    error: statusError,
    callFetch: callFetchStatus
  } = useFetch("POST");
  const { enqueueSnackbar } = useSnackbar();

  const connectionOptions = useMemo(
    () => [
      { value: "manual" as RTSPSources, label: t["Manual"] },
      { value: "virtuose" as RTSPSources, label: "Virtuose" },
      { value: "acc_token" as RTSPSources, label: t["Managed by VXG Cloud"] }
    ],
    [t]
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      if (!camera) return;
      const body: Record<string, unknown> = {};
      if (rtspSource === "manual") {
        if (state.rtsp !== camera.rtsp) {
          body.rtsp = state.rtsp;
        }
      } else if (rtspSource === "acc_token") {
        if (state.token !== camera.acc_token) {
          body.acc_token = state.token;
        }
      } else if (state.token !== camera.eagleEyeToken) {
        body.eagleEyeToken = state.token;
        body.cameraESN = state.esn;
      }
      if (camera.rtspSource !== rtspSource) {
        body.rtspSource = rtspSource;
      }
      if (camera.boxId !== state.boxId && state.boxId) {
        body.boxId = state.boxId;
      }
      if (camera.webHooks !== state.webHooks) {
        body.webHooks = state.webHooks;
      }
      if (camera.name !== state.name) {
        body.name = state.name;
      }
      const savingModels = modelsReady
        ? activeModels.filter((item) => enabledModels.includes(item.model))
        : activeModels;
      body.models = savingModels.map((model) => model.model);
      body.activeModels = savingModels;

      callFetchPut(ENDPOINTS.cameras + `/${camera._id}`, {
        body: JSON.stringify(body)
      }).catch(console.error);
    },
    [state, rtspSource, activeModels, enabledModels, modelsReady, camera, callFetchPut]
  );

  const onUpdateCameraStatus = useCallback(
    (cameraStatus: "connected" | "disconnected") => {
      if (!camera) return;
      const formData = new FormData();
      formData.append("cameraStatus", cameraStatus);
      callFetchStatus(ENDPOINTS.cameras + `/${camera._id}`, {
        body: formData,
        headers: {} as any
      })
        .then(() => setCurrentStatus(cameraStatus))
        .catch(console.error);
    },
    [camera, callFetchStatus]
  );

  const onChangeActiveModels = useCallback((model: ModelTypes, video: boolean, audio: boolean) => {
    const flags = normalizeActiveModelMedia(model, video, audio);
    setActiveModels((prevState) => {
      if (flags.video === false && flags.audio === false) {
        return prevState.filter((elm) => elm?.model !== model);
      }
      const index = prevState.findIndex((elm) => elm?.model === model);
      if (index === -1) {
        return [...prevState, { model, ...flags }];
      }
      return prevState.map((elm, i) => (i === index ? { model, ...flags } : elm));
    });
  }, []);

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
    }
  }, [response, onClose, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (statusResponse) {
      enqueueSnackbar(statusResponse.message || "", { variant: "success" });
      onSuccess?.();
    }
  }, [statusResponse, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (statusError) {
      enqueueSnackbar(statusError.message || "", { variant: "error" });
    }
  }, [statusError, enqueueSnackbar]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    const models: ActiveModel[] = [];
    if (camera.activeModels?.length) {
      camera.activeModels.forEach((item) => {
        const flags = normalizeActiveModelMedia(item.model, Boolean(item.video), Boolean(item.audio));
        if (flags.video || flags.audio) {
          models.push({ model: item.model, ...flags });
        }
      });
    } else if (camera.models?.length) {
      camera.models.forEach((model) => {
        const flags = normalizeActiveModelMedia(model, true, true);
        models.push({ model, ...flags });
      });
    }
    const allowed = camera.box?.site ? filterModelsForUI(camera.box.site.enabledModels) : null;
    setActiveModels(allowed ? models.filter((item) => allowed.includes(item.model)) : models);
  }, [camera.activeModels, camera.models, camera.box?.site]);

  // Stored URLs and tokens are never sent back, so they start blank; blank keeps the stored value
  // unless the connection type is being switched.
  const keepsSecret = camera.rtspSource === rtspSource;
  const secretPlaceholder = t["Leave blank to keep the current value"] || "Leave blank to keep the current value";

  const renderConnectionFields = () => {
    if (rtspSource === "acc_token") {
      return (
        <TextInput
          value={state.token}
          label={t["Token"]}
          type="text"
          placeholder={keepsSecret ? secretPlaceholder : "**************"}
          error={!!errors.token}
          helperText={errors.token}
          startAdornment={<LinkOutlined sx={iconSx} />}
          sx={fieldSx}
          onChange={({ target: { value } }) => {
            setState((prevState) => ({ ...prevState, token: value }));
            setErrors((prevState) => ({ ...prevState, token: "" }));
          }}
          fullWidth
          required={!keepsSecret}
        />
      );
    }

    if (rtspSource === "eagleEyeToken") {
      return (
        <Fragment>
          <TextInput
            value={state.esn}
            label={t["ESN No"]}
            type="text"
            placeholder={t["Enter camera ESN no"]}
            error={!!errors.esn}
            helperText={errors.esn}
            startAdornment={<LinkOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, esn: value }));
              setErrors((prevState) => ({ ...prevState, esn: "" }));
            }}
            fullWidth
            required
          />
          <TextInput
            value={state.token}
            label={t["Token"]}
            type="text"
            placeholder={keepsSecret ? secretPlaceholder : "**************"}
            error={!!errors.token}
            helperText={errors.token}
            startAdornment={<LinkOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, token: value }));
              setErrors((prevState) => ({ ...prevState, token: "" }));
            }}
            fullWidth
            required={!keepsSecret}
          />
        </Fragment>
      );
    }

    return (
      <TextInput
        value={state.rtsp}
        label={t["RTSP URL"]}
        type="text"
        placeholder={keepsSecret ? secretPlaceholder : t["rtsp://camera-stream-url"]}
        error={!!errors.rtsp}
        helperText={errors.rtsp}
        startAdornment={<LinkOutlined sx={iconSx} />}
        sx={fieldSx}
        onChange={({ target: { value } }) => {
          setState((prevState) => ({ ...prevState, rtsp: value }));
          setErrors((prevState) => ({ ...prevState, rtsp: "" }));
        }}
        fullWidth
        required={!keepsSecret}
      />
    );
  };

  return (
    <MyModal
      title={t["Edit Camera"]}
      subtitle={t["Edit Camera description"]}
      open={true}
      setOpen={onClose}
      variant="light"
      width={cameraModalWidth}
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <ConnectionTypeSelect
            label={t["Connection Type"]}
            value={rtspSource}
            onChange={setRTSPSource}
            options={connectionOptions}
          />

          {rtspSource === "virtuose" && camera.cameraIpAndProfile ? (
            <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 13 }}>
              {t["Camera IP & Profile"]}: {camera.cameraIpAndProfile}
            </Typography>
          ) : null}

          <Box>
            <Typography sx={sectionTitleSx}>{t["Camera Information"]}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput
                  value={state.name}
                  label={t["Camera Name"]}
                  type="text"
                  placeholder={t["e.g Camera Room 67"]}
                  error={!!errors.name}
                  helperText={errors.name}
                  startAdornment={<VideocamOutlined sx={iconSx} />}
                  sx={fieldSx}
                  onChange={({ target: { value } }) => {
                    setState((prevState) => ({ ...prevState, name: value }));
                    setErrors((prevState) => ({ ...prevState, name: "" }));
                  }}
                  fullWidth
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {rtspSource === "manual" ? renderConnectionFields() : null}
              </Grid>
              {rtspSource !== "manual" ? (
                <Grid item xs={12}>
                  {renderConnectionFields()}
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <TextInput
                  value={state.webHooks}
                  label={t["Webhook URL Optional"]}
                  type="text"
                  placeholder={t["https://example.com/webhook"]}
                  error={!!errors.webHooks}
                  helperText={errors.webHooks}
                  startAdornment={<LinkOutlined sx={iconSx} />}
                  sx={fieldSx}
                  onChange={({ target: { value } }) => {
                    setState((prevState) => ({ ...prevState, webHooks: value }));
                    setErrors((prevState) => ({ ...prevState, webHooks: "" }));
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {modelsReady ? (
            <ActivationModelGrid
              enabledModels={enabledModels}
              activeModels={activeModels}
              onChange={onChangeActiveModels}
              title={t["Activation Models"]}
              description={t["Activation Models description"]}
              hint={t["At least one activation model is required."]}
              videoLabel={t["Video"]}
              audioLabel={t["Audio"]}
              mediaProcessingLabel={t["Media Processing"]}
              t={t}
            />
          ) : null}

          <Box>
            <Typography sx={sectionTitleSx}>{t["Surveillance Box"]}</Typography>
            <BoxSelect
              value={state.boxId}
              hideLabel
              startAdornment={<Inventory2Outlined sx={iconSx} />}
              onChange={(boxId) => setState((prevState) => ({ ...prevState, boxId: boxId ? boxId : "" }))}
              onBox={(box) => {
                if (!box?.site) {
                  setModelsReady(false);
                  return;
                }
                const nextModels = filterModelsForUI(box.site.enabledModels);
                setModelsReady(true);
                setEnabledModels(nextModels);
                setActiveModels((prev) => prev.filter((item) => nextModels.includes(item.model)));
              }}
            />
            <Typography sx={sectionHintSx}>{t["Surveillance Box helper"]}</Typography>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <Link
              to={"/cameras/" + camera._id}
              style={{
                textDecoration: "none",
                color: COLORS.primary,
                fontSize: 14,
                fontWeight: 500
              }}
            >
              {t["Edit region of interests"]}
            </Link>
            {currentStatus === "connected" ? (
              <Button
                type="button"
                variant="outlined"
                color="error"
                disabled={isStatusLoading}
                endIcon={isStatusLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                onClick={() => onUpdateCameraStatus("disconnected")}
                sx={{
                  textTransform: "none",
                  borderRadius: `${COLORS.radius.md}px`,
                  fontWeight: 500
                }}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                type="button"
                variant="outlined"
                color="success"
                disabled={isStatusLoading}
                endIcon={isStatusLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                onClick={() => onUpdateCameraStatus("connected")}
                sx={{
                  textTransform: "none",
                  borderRadius: `${COLORS.radius.md}px`,
                  fontWeight: 500
                }}
              >
                Connect
              </Button>
            )}
          </Box>
        </Stack>

        <FormModalActions onCancel={onClose} cancelLabel={t["Cancel"]} submitLabel={t["Update"]} loading={isLoading} />
      </Box>
    </MyModal>
  );
}
